// src/components/ActionBoardContent/ActionBoardGrid.js

import React from 'react';
import { Spinner } from 'react-bootstrap';
import { IconTarget } from '@tabler/icons-react';
import { useSelector } from 'react-redux';
import ActionAreaAccordion from './ActionAreaAccordion';
import './ActionBoardGrid.css';

export const ActionBoardGrid = ({
    actionAreas,
    handlers,
    onOpenMeasureOffcanvas,
    unitId,
    units = [],
    unitLeaderMap = {},
}) => {
    // Retrieve all actionAreaStatuses at once
    const actionAreaStatuses = useSelector((state) => state.measures.actionAreaStatuses);

    const unitNameLookup = React.useMemo(() => {
        const map = {};
        units.forEach((unit) => {
            map[unit.id] = unit.unit_name;
        });
        return map;
    }, [units]);

    const canEditUnit = (actionAreaUnitId) => !!unitLeaderMap[actionAreaUnitId];

    // Group action areas by unit
    const actionAreasByUnit = React.useMemo(() => {
        const grouped = {};
        actionAreas.forEach((actionArea) => {
            const unitIdKey = actionArea.unit;
            if (!grouped[unitIdKey]) {
                grouped[unitIdKey] = [];
            }
            grouped[unitIdKey].push(actionArea);
        });
        return grouped;
    }, [actionAreas]);

    return (
        <div className="action-board-grid">
            {/* Empty State */}
            {actionAreas.length === 0 && (
                <div className="empty-state">
                    <IconTarget size={48} stroke={1} className="empty-state-icon" />
                    <h4>Noch keine Handlungsfelder vorhanden</h4>
                    <p className="text-muted">
                        Erstelle Dein erstes Handlungsfeld, um mit der Planung von Maßnahmen zu beginnen.
                    </p>
                </div>
            )}

            {/* Action Areas grouped by Unit */}
            {Object.entries(actionAreasByUnit).map(([groupUnitId, unitActionAreas]) => {
                const owningUnitName = unitNameLookup[groupUnitId] || 'Unbekannte Einheit';
                const isLeaderForUnit = canEditUnit(groupUnitId);

                return (
                    <div key={groupUnitId} className="unit-action-areas-group">
                        {/* Unit Header */}
                        <div className="unit-group-header">
                            <span className="unit-group-name">{owningUnitName.toUpperCase()}</span>
                        </div>

                        {/* Action Areas for this Unit */}
                        <div className="unit-action-areas-list">
                            {unitActionAreas.map((actionArea) => {
                                const actionAreaStatus = actionAreaStatuses[actionArea.id] || 'idle';
                                
                                // Show loading state if the action area is still loading
                                if (actionAreaStatus === 'loading') {
                                    return (
                                        <div key={actionArea.id} className="action-area-loading">
                                            <Spinner animation="border" role="status" size="sm">
                                                <span className="visually-hidden">Loading...</span>
                                            </Spinner>
                                            <span className="ms-2">Wird geladen...</span>
                                        </div>
                                    );
                                }

                                return (
                                    <ActionAreaAccordion
                                        key={actionArea.id}
                                        actionArea={actionArea}
                                        onSaveActionArea={handlers.saveActionArea}
                                        isReadOnly={!isLeaderForUnit}
                                        unitName={owningUnitName}
                                        shouldOpenOffcanvas={actionArea.id === handlers.newActionAreaId}
                                        onOffcanvasClose={() => handlers.setNewActionAreaId(null)}
                                    />
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default ActionBoardGrid;